import { gql, useMutation, useQuery } from '@apollo/client'
import flatten from 'lodash/flatten'
import set from 'lodash/set'
import { useMemo } from 'react'

import {
  ArtistSubscriptionPlan,
  ArtistSubscriptionPlanQuery,
  ArtistSubscriptionPlanQueryVariables,
  ArtistUpdateItemMutation,
  ArtistUpdateItemMutationVariables,
  ConsoleItemEditQuery,
  ConsoleItemEditQueryVariables,
  ItemSelectBoxFragmentDoc,
  ItemType,
  MembershipInterval,
  PersonalInformationType,
  SalesStatus,
} from '@/clients/utoniq-core/schema'
import { personalInformationRequirementsValue } from '@/components/pages/ConsoleItemCreate/template/components'
import {
  getSkuSettings,
  getThroughTicket,
} from '@/components/pages/ConsoleItemCreate/viewModel'
import {
  useMessage,
  useRouter,
  useTranslation,
  useUserTmpUploader,
} from '@/core'
import { makeViewModel } from '@/core/architecture'
import { useCurrentUser } from '@/core/auth'
import { getResource } from '@/lib/resourceUpload'
import { createUuid } from '@/lib/uuid'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { t } = useTranslation('consoleItemEdit')
  const { router } = useRouter<{ itemId: string }>()
  const { showError } = useMessage()
  const { user } = useCurrentUser()
  const { upload, progress } = useUserTmpUploader(user?.uid || '')
  const { data } = useQuery<
    ConsoleItemEditQuery,
    ConsoleItemEditQueryVariables
  >(QUERY, {
    variables: {
      itemId: router.query.itemId,
    },
  })
  const [artistUpdateItem, { loading: updating }] = useMutation<
    ArtistUpdateItemMutation,
    ArtistUpdateItemMutationVariables
  >(MUTATION)

  const { data: artistSubscriptionPlanData } = useQuery<
    ArtistSubscriptionPlanQuery,
    ArtistSubscriptionPlanQueryVariables
  >(ArtistSubscriptionPlan)

  const plans = useMemo(() => {
    if (artistSubscriptionPlanData?.artist?.subscription?.products?.length) {
      return flatten(
        artistSubscriptionPlanData?.artist?.subscription?.products.map(
          product => {
            const monthPlan = product.plans.find(
              plan => plan.interval === MembershipInterval.Month
            )
            if (monthPlan) {
              return [{ ...monthPlan, title: product.title }]
            }
            return product.plans.map(plan => ({
              ...plan,
              title: product.title,
            }))
          }
        )
      )
    }
    if (artistSubscriptionPlanData?.artist?.subscription?.plans?.length) {
      return artistSubscriptionPlanData.artist.subscription.plans
    }
    return []
  }, [
    artistSubscriptionPlanData?.artist?.subscription?.plans,
    artistSubscriptionPlanData?.artist?.subscription?.products,
  ])

  const currentPlans = useMemo(() => {
    if (data?.item) {
      // isPremiumがtrueで、purchesablePlanIdsが空の場合は、全てのプランを選択
      if (
        data.item.isPremium &&
        (!data.item.purchesablePlanIds ||
          data.item.purchesablePlanIds?.length === 0)
      ) {
        return plans.map(plan => plan?.planId ?? '')
      }
      return data.item.purchesablePlanIds ?? []
    }
    return []
  }, [data?.item, plans])

  return {
    state: {
      artistId: user?.uid ?? '',
      item: data?.item ?? null,
      plans,
      currentPlans,
      updating,
      progress,
    },
    actions: {
      /**
       * ファイルアップロード時に呼ばれる処理
       * @param {ItemType} resourceType リソース型
       * @param {boolean} hasCreateResource 更新の際はデフォルトfalse
       */
      async onDrop(resourceType, _ = false) {
        const file = await getResource(resourceType).catch(error => {
          showError(new Error(t([error.message, 'interruptedUpload'])))
        })
        if (!file) {
          return
        }
        const { tmpPath, fileUrl } = await upload(
          file,
          [ItemType.Audio, ItemType.Video, ItemType.Magazine].includes(
            resourceType
          ),
          true
        )
        const uuid = createUuid()

        return {
          id: uuid,
          resourceName: uuid,
          imageUrl: fileUrl,
          imageUploadPath: tmpPath,
        }
      },
      async onSubmit({
        hasSku,
        description,
        paymentType,
        price,
        title,
        issuedNumber,
        thumbnail,
        thumbnailBack,
        purchaseLimitByUser,
        resource,
        wallpaperResource,
        resaleEnabled,
        skuSettings,
        type,
        salesStatus,
        salesStartedAt,
        salesEndedAt,
        metadata,
        personalInformationRequirements,
        hideSalesNumber,
        images,
        isPremium,
        purchesablePlanIds,
        purchaseLimitPerOrder,
        isDownloadable,
        isBothThumbnail,
      }) {
        /**
         * 壁紙リソースの編集
         * 変更なければundefinedを渡す
         */
        const wallpaperResources =
          type === 'wallpaper' && wallpaperResource?.imageUploadPath
            ? {
                wallpaperResources: {
                  wallpaperResourcePath: wallpaperResource?.imageUploadPath,
                },
              }
            : undefined
        /**
         * オーディオ・動画・マガジンリソースの編集
         * 変更なければundefinedを渡す
         */
        const resources = resource?.imageUploadPath
          ? {
              resource: {
                tmpPath: resource?.imageUploadPath,
              },
            }
          : undefined

        if (type === ItemType.Ticket && !resaleEnabled && metadata) {
          set(metadata, 'ticket.resalePeriod', undefined)
        }
        if (type === ItemType.Ticket && metadata) {
          set(
            metadata,
            'ticket.throughTicket',
            getThroughTicket(
              metadata.ticket?.isThroughTicket
                ? metadata.ticket?.throughTicket
                : null
            )
          )
          if (metadata.ticket?.isThroughTicket) {
            set(metadata, 'ticket.startDateOfUse', null)
            set(metadata, 'ticket.endDateOfUse', null)
          }
        }
        if (type === ItemType.Ticket && metadata?.ticket?.startAssignedNumber) {
          set(
            metadata,
            'ticket.startAssignedNumber',
            +metadata.ticket.startAssignedNumber
          )
        }
        const inputtedSkuSettings = getSkuSettings(
          hasSku,
          issuedNumber,
          skuSettings
        )
        // SKUを設定しない場合の送信値。 元から設定していない場合はundefined、設定していた場合はnullを送信する
        const falsySkuSettings = data?.item?.skuSettings ? null : undefined

        // 個人情報必須の区分け
        let personalInformationRequirementsInput:
          | PersonalInformationType[]
          | undefined = undefined
        if (type === ItemType.Ticket) {
          switch (personalInformationRequirements) {
            case personalInformationRequirementsValue.nameAndTel:
              personalInformationRequirementsInput = [
                PersonalInformationType.FullName,
                PersonalInformationType.PhoneNumber,
              ]
              break
            case personalInformationRequirementsValue.nameAndTelAndAddress:
              personalInformationRequirementsInput = [
                PersonalInformationType.FullName,
                PersonalInformationType.PhoneNumber,
                PersonalInformationType.Address,
              ]
              break
            default:
              personalInformationRequirementsInput = []
              break
          }
        }

        await artistUpdateItem({
          variables: {
            artistUpdateItemId: router.query.itemId,
            inputs: {
              description,
              paymentType,
              price,
              personalInformationRequirements:
                personalInformationRequirementsInput,
              title,
              issuedNumber,
              thumbnail,
              thumbnailBack,
              isBothThumbnail,
              salesStatus: salesStatus
                ? SalesStatus.Active
                : SalesStatus.Stopped,
              salesStartedAt,
              salesEndedAt,
              purchaseLimitByUser,
              resaleEnabled,
              purchaseLimitPerOrder,
              isDownloadable,
              metadata,
              hideSalesNumber,
              isPremium,
              purchesablePlanIds: isPremium ? purchesablePlanIds : [],
              skuSettings: inputtedSkuSettings ?? falsySkuSettings,
              images: images?.filter(img => !!img.tmpPath),
              ...wallpaperResources,
              ...resources,
            },
          },
        })
      },
    },
  }
})

const QUERY = gql`
  query ConsoleItemEdit($itemId: ID!) {
    item(id: $itemId) {
      ...ItemSelectBox
      preSale {
        id
      }
      metadata {
        magazine {
          pageDirection
        }
        ticket {
          expirationTerm
          note
          description
          assignedNumberPrefix
          startAssignedNumber
          isThroughTicket
          throughTicket {
            name
            startDateOfUse
            endDateOfUse
          }
          distributionEnabled
          distributionEndedAt
          startDateOfUse
          endDateOfUse
        }
        merch {
          receptionPeriod {
            endedAt
          }
        }
        magazine {
          pageDirection
        }
        wallpaper {
          isPrintEnabled
          printCount
          printType
        }
      }
    }
  }
  ${ItemSelectBoxFragmentDoc}
`
const MUTATION = gql`
  mutation ArtistUpdateItem(
    $artistUpdateItemId: ID!
    $inputs: ArtistUpdateItemInput
  ) {
    artistUpdateItem(id: $artistUpdateItemId, inputs: $inputs) {
      ...ItemSelectBox
    }
  }
  ${ItemSelectBoxFragmentDoc}
`
